/**
 * 人员选择器 工具方法
 * @author sunzhiguang
 * @date 2020/9/23
 */

import { Employee, Session, UID } from "@/types/chat";
import {
  convertEmployeeToString,
  employeeToUid,
  isCrossPlatformGroupSession,
  isExternalGroupSession
} from "@/utils/chat/message";
import GroupRoster from "@jd/jdee.im.sdk/lib/es/model/Roster";
import { ListItemData } from "@/components/ListItem";
import ImService from "@/server/ImService";
import get from "lodash/get";
import { UserSelectionResult } from "@jd/focus-desktop-comps/lib/es/UserSelectionModal";
import { GroupKind } from "@jd/jdee.im.sdk/lib/es/model/Group";
import pick from "lodash/pick";
import { convertUidToString } from "@jd/jdee.im.sdk/lib/es/utils/utils";
import { getDeptPositionDesc } from "@/models/user";
import { setUserTypeChange } from "@/components/Tags";
import { getStrangerToGroupRecord } from "@/api/chat";

export function mapGroupRosters(
  groupRosters: Partial<GroupRoster>[],
  currentEmployee: any,
  opts?: { checked?: boolean; visible?: boolean }
): ListItemData[] {
  const dataList = groupRosters.map((roster) => {
    const employee = roster.info as Employee;
    let id = "";
    if (employee) {
      id = convertEmployeeToString(employee);
    } else {
      id = convertUidToString(roster.user as UID);
    }
    const employeeExt = employee?.employeeExt || {};
    if (!employee) {
      // eslint-disable-next-line no-console
      console.error("debug: employee exception roster item =>", JSON.stringify(roster));
    }
    const userType = setUserTypeChange(
      { ...employee, userType: employeeExt.userType },
      currentEmployee
    );
    return {
      ...employee,
      id: id,
      name: employee?.name,
      avatar: employee?.avatar,
      checked: true,
      userId: employee?.userId,
      app: employee?.app,
      teamId: employee?.teamId,
      visible: false,
      accountType: roster.info?.accountType,
      ...opts,
      desc: getDeptPositionDesc({
        deptName: employee?.deptName || "",
        titleName: employee?.titleName || "",
        unitName: employee?.unitName,
        userType
      }),
      remark: employeeExt.remark
    };
  });
  return dataList.filter((item) => item.name);
}

export async function mapInviteStranger(sessionId: string) {
  // 待入群列表
  const [data] = await getStrangerToGroupRecord({
    groupId: sessionId
  });
  const strangerList: [] = data?.strangerToGroupInfoList;
  if (!strangerList || strangerList.length === 0) {
    return;
  }
  const params = strangerList.map((item: any) => {
    return {
      appId: item.strangerApp,
      teamId: item.strangerTeamId,
      userId: item.strangerId
    };
  });
  const memberInfo = await ImService.getInstance().getChangeUserDetailList(params);
  const newMemberInfo = memberInfo.map((item: any) => {
    return {
      user: { ...item, userType: item.employeeExt.userType },
      info: { ...item, fromGlobalSearch: "1" },
      invitingmember: true,
      ...item,
      checked: true,
      visible: false,
      desc: getDeptPositionDesc({
        deptName: item?.deptName || "",
        titleName: item?.titleName || "",
        unitName: item?.unitName || "",
        userType: item?.employeeExt?.userType || "3"
      }),
      remark: item?.employeeExt?.remark,
      id: convertEmployeeToString(item)
    };
  });
  const newList = newMemberInfo.reduce((arr1: any, arr2: any) => {
    let isFlag = false;
    arr1.forEach((item: any) => {
      if (item.strangerId === arr2.user.userId) {
        isFlag = true;
        item = Object.assign(item, arr2);
      }
    });
    if (!isFlag) {
      arr1.push(arr2);
    }
    return arr1;
  }, strangerList);
  return newList;
}

export function mapSessionMembers(sessionMembers: Partial<Employee>[]): ListItemData[] {
  return sessionMembers.reduce((result, item) => {
    if (item.userId !== "all") {
      const employeeExt = item.employeeExt || {};
      result.push({
        id: convertEmployeeToString(item as Employee),
        name: item.name,
        avatar: item.avatar,
        userId: item.userId,
        teamId: item.teamId,
        labelInfoList: item.labelInfoList,
        desc: getDeptPositionDesc({
          deptName: item?.deptName || "",
          titleName: item?.titleName || "",
          unitName: item?.unitName,
          userType: employeeExt.userType
        }),
        origin: { info: item, uid: employeeToUid(item as Employee) }
      });
    }
    return result;
  }, [] as ListItemData[]);
}

export function mapSelectorResult(data: UserSelectionResult): UID[] {
  const result = get(data, "data.result", []) as ListItemData[];
  const result_stranger = get(data, "data.strangerResult", []) as ListItemData[];
  if (result.length === 0 && result_stranger.length === 0) {
    return [];
  }
  const strangerList = result_stranger.map((item) => {
    return {
      ...item,
      fromGlobalSearch: "1",
      origin: {
        uid: {
          app: item.app,
          pin: item.userId,
          teamId: item.teamId
        }
      }
    };
  });
  const allResult = [...result, ...strangerList];
  const selectData = allResult.filter((item) => !item.checked);
  if (!selectData || selectData.length === 0) {
    return [];
  }
  return selectData.map((item) => {
    return {
      ...item?.origin?.uid,
      name: item.name,
      userType: item.userType,
      fromGlobalSearch: item?.fromGlobalSearch || "0"
    };
  });
}

export function mergeGroupRoster(invitees: UID[], groupRosters: Partial<GroupRoster>[]) {
  const rosters = groupRosters.map((item) => pick(item.user, ["pin", "app", "teamId"]));
  return invitees.concat((rosters || []) as UID[]);
}

export enum CheckInviteeResult {
  Normal = "normal",
  External = "external",
  CrossPlatform = "crossPlatform"
}

/**
 * 226版本把包含外部联系人和跨租户的群归为内部群，内部群邀请这两类人不用再创建一个新群
 */
export function checkInvitee(
  invitees: UID[],
  currentEmployee: Employee,
  session?: Session
): CheckInviteeResult {
  let index: number;
  // 邀请人
  if (session && isCrossPlatformGroupSession(session)) {
    return CheckInviteeResult.Normal;
  }
  // 如果这个群是个外部群，但是不是跨平台的外部群，再拉个跨平台的人的话，就走跨平台群的创建逻辑
  if (session && isExternalGroupSession(session)) {
    index = invitees.findIndex((item) => item.userType === "5");
    if (index > -1) {
      return CheckInviteeResult.CrossPlatform;
    }
    return CheckInviteeResult.Normal;
  }
  // 创建群
  index = invitees.findIndex((item) => item.userType === "5");
  if (index > -1) {
    return CheckInviteeResult.CrossPlatform;
  }
  // index = invitees.findIndex((item) => item.userType === "1" || item.userType === "4");
  // if (index > -1) {
  //   return CheckInviteeResult.External;
  // }
  return CheckInviteeResult.Normal;
}

/**
 * 邀请入群
 * @param sessionId
 * @param invitees
 */
export function inviteAddGroupApply(sessionId: string, invitees: UID[]) {
  return ImService.getInstance().inviteAddGroupApply(
    sessionId,
    invitees,
    "application_noreply" // application_noreply 拒绝后不可以再次审请
  );
}
// tip:再次邀请的时候
// eslint-disable-next-line max-params
export function inviteUser(
  sessionId: string,
  invitees: UID[],
  reason?: string,
  inviteStrangerAgain?: string,
  options?: {
    noEmit?: boolean;
    noSaveDB?: boolean;
    shareMsgCount?: number;
  }
) {
  return ImService.getInstance().inviteUser(
    sessionId,
    invitees,
    reason || "",
    "",
    options,
    inviteStrangerAgain || "0"
  );
}

// eslint-disable-next-line max-params
export function createGroupSession(opts: CreateGroupOption) {
  const { invitees, name, intro, max } = opts;
  const params: any = {
    name,
    kind: GroupKind.INNER,
    subKind: 5,
    intro
  };
  if (max) params.max = max;
  return ImService.getInstance().createGroupSession(invitees, "", params);
}
interface CreateGroupOption {
  invitees: UID[];
  name: string;
  intro?: string;
  max?: number;
}
export function createExternalGroup(params: CreateGroupOption) {
  const { invitees, name, intro, max } = params;
  return ImService.getInstance().createGroupSession(invitees, "", {
    name,
    kind: GroupKind.EXTERNAL,
    subKind: 6,
    intro,
    max
  });
}

export function createCrossPlatformGroup(params: CreateGroupOption) {
  const { invitees, name, intro, max } = params;
  return ImService.getInstance().createGroupSession(invitees, "", {
    name,
    kind: GroupKind.EXTERNAL,
    subKind: 3,
    intro,
    max
  });
}
